import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, fmtUSD, fmtDate, SITUATIONS, OFFER_TYPES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, MessageSquare, Calendar, FileText, Check, X, Clock, Loader2, Send } from "lucide-react";

export default function PropertyDetail() {
  const { pid } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prop, setProp] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiCompare, setAiCompare] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [showOffer, setShowOffer] = useState(false);
  const [contactCtx, setContactCtx] = useState(null); // {recipient_id, recipient_name}
  const [apptCtx, setApptCtx] = useState(null);

  const startChat = async (recipient_id, recipient_name) => {
    setContactCtx({ recipient_id, recipient_name });
  };
  const sendQuickMsg = async (body) => {
    if (!contactCtx) return;
    try {
      await api.post("/messages", { recipient_id: contactCtx.recipient_id, body, property_id: pid });
      toast.success("Message sent");
      setContactCtx(null);
      navigate("/messages");
    } catch { toast.error("Send failed"); }
  };
  const scheduleAppt = (other_party_id) => setApptCtx({ other_party_id });
  const saveAppt = async (payload) => {
    try {
      await api.post("/appointments", { ...payload, property_id: pid, other_party_id: apptCtx.other_party_id });
      toast.success("Appointment requested");
      setApptCtx(null);
      navigate("/appointments");
    } catch { toast.error("Could not schedule"); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${pid}`);
      setProp(data);
      const isMineOrAdmin = user?.role === "admin" || data.seller_id === user?.user_id;
      if (isMineOrAdmin) {
        const { data: o } = await api.get(`/offers/by-property/${pid}`);
        setOffers(o);
      }
    } catch (e) {
      toast.error("Could not load property");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  const respond = async (offer_id, action) => {
    try {
      await api.post(`/offers/${offer_id}/respond`, { action });
      toast.success(`Offer ${action}ed`);
      load();
    } catch { toast.error("Failed"); }
  };

  const runCompare = async () => {
    setComparing(true);
    try {
      const { data } = await api.post("/ai/compare-offers", { property_id: pid });
      setAiCompare(data);
    } catch { toast.error("Compare failed"); } finally { setComparing(false); }
  };

  if (loading) return <DashboardLayout><div className="text-[var(--adn-text-soft)] text-sm">Loading…</div></DashboardLayout>;
  if (!prop) return null;

  const isOwner = user?.user_id === prop.seller_id;
  const isProvider = ["investor","realtor","attorney","lender","contractor","title"].includes(user?.role);

  return (
    <DashboardLayout>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="eyebrow">Property</p>
          <h1 className="font-display text-3xl font-semibold mt-2 leading-tight">{prop.address_line1}</h1>
          <p className="text-[var(--adn-text-soft)] mt-1">{prop.city}, {prop.state} {prop.zip_code}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isProvider && (
            <>
              <button data-testid="open-offer" onClick={() => setShowOffer(true)} className="btn-gold px-5 py-3 rounded text-sm">Submit Offer</button>
              <button data-testid="contact-seller" onClick={() => startChat(prop.seller_id, "Seller")} className="btn-outline-navy px-5 py-3 rounded text-sm inline-flex items-center gap-2">
                <MessageSquare size={16} /> Message Seller
              </button>
              <button data-testid="schedule-walkthrough" onClick={() => scheduleAppt(prop.seller_id)} className="btn-outline-navy px-5 py-3 rounded text-sm inline-flex items-center gap-2">
                <Calendar size={16} /> Request Walkthrough
              </button>
            </>
          )}
          {isOwner && (
            <button data-testid="run-compare" onClick={runCompare} disabled={comparing} className="btn-outline-navy px-5 py-3 rounded text-sm inline-flex items-center gap-2">
              <Sparkles size={16} /> {comparing ? "Comparing…" : "AI Compare Offers"}
            </button>
          )}
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-white border border-[var(--adn-line)] p-6">
            <p className="eyebrow">Situation & details</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(prop.situations || []).map((s) => {
                const meta = SITUATIONS.find(x => x.id === s);
                return <span key={s} className={`pill ${meta?.pill || "pill-neutral"}`}>{meta?.label || s}</span>;
              })}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-6 text-sm">
              <Stat label="Type" v={prop.property_type} />
              <Stat label="Beds / Baths" v={`${prop.bedrooms || "—"} / ${prop.bathrooms || "—"}`} />
              <Stat label="Sq Ft" v={prop.square_feet || "—"} />
              <Stat label="Year" v={prop.year_built || "—"} />
              <Stat label="Est. Value" v={fmtUSD(prop.estimated_value)} />
              <Stat label="Mortgage" v={fmtUSD(prop.mortgage_balance)} />
              <Stat label="Months Behind" v={prop.months_behind ?? "—"} />
              <Stat label="Timeline" v={prop.timeline || "—"} />
            </div>
            {prop.notes && <p className="mt-5 text-sm text-[var(--adn-text-soft)] leading-relaxed">{prop.notes}</p>}
          </section>

          {prop.ai && (
            <section className="bg-white border-l-4 border-l-[var(--adn-gold)] border-y border-r border-[var(--adn-line)] p-6">
              <div className="flex items-center gap-2">
                <Sparkles className="text-[var(--adn-gold)]" size={18} />
                <p className="eyebrow">AI summary</p>
              </div>
              <p className="mt-3 text-[15px] leading-relaxed">{prop.ai.summary}</p>
              <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-5">
                <Stat label="Distress score" v={`${prop.ai.distress_score}/100`} accent />
                <Stat label="Equity estimate" v={fmtUSD(prop.ai.equity_estimate)} />
                <Stat label="Generated" v={fmtDate(prop.ai.generated_at)} />
              </div>
              {prop.ai.solution_paths?.length > 0 && (
                <div className="mt-6">
                  <p className="eyebrow mb-3">Possible paths</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {prop.ai.solution_paths.map((s, i) => (
                      <div key={i} className="border border-[var(--adn-line)] p-4">
                        <div className="font-display font-semibold">{s.name}</div>
                        <div className="text-sm text-[var(--adn-text-soft)] mt-1">{s.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {prop.ai.next_steps?.length > 0 && (
                <div className="mt-6">
                  <p className="eyebrow mb-3">Recommended next steps</p>
                  <ul className="space-y-2">
                    {prop.ai.next_steps.map((n, i) => (
                      <li key={i} className="text-sm flex items-start gap-2"><Check size={16} className="text-[var(--adn-emerald)] mt-0.5" /> {n}</li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )}

          {(isOwner || user?.role === "admin") && (
            <section className="bg-white border border-[var(--adn-line)] p-6">
              <div className="flex items-center justify-between">
                <p className="eyebrow">Offers ({offers.length})</p>
              </div>
              {aiCompare && (
                <div className="mt-4 bg-[var(--adn-navy)] text-white p-5">
                  <p className="eyebrow text-[var(--adn-gold)]">AI comparison</p>
                  <p className="mt-2 text-sm leading-relaxed">{aiCompare.summary}</p>
                </div>
              )}
              {offers.length === 0 ? (
                <p className="mt-4 text-sm text-[var(--adn-text-soft)]">No offers yet. Providers will respond shortly.</p>
              ) : (
                <div className="mt-4 space-y-3">
                  {offers.map((o) => (
                    <OfferRow
                      key={o.offer_id}
                      offer={o}
                      onAccept={() => respond(o.offer_id, "accept")}
                      onReject={() => respond(o.offer_id, "reject")}
                      canRespond={isOwner}
                      onMessage={() => startChat(o.provider_id, o.provider_name)}
                      onSchedule={() => scheduleAppt(o.provider_id)}
                    />
                  ))}
                </div>
              )}
            </section>
          )}
        </div>

        <aside className="space-y-6">
          <section className="bg-white border border-[var(--adn-line)] p-6">
            <p className="eyebrow">Closing checklist</p>
            <ul className="mt-4 space-y-3 text-sm">
              {[
                "Property submitted",
                "AI analysis complete",
                "Offers received",
                "Offer accepted",
                "Title work started",
                "Closing scheduled",
                "Funds received",
              ].map((step, i) => {
                const done = (i === 0) || (i === 1 && prop.ai) || (i === 2 && offers.length > 0) || (i === 3 && offers.some(x => x.status === "accepted"));
                return (
                  <li key={step} className="flex items-center gap-2">
                    <span className={`h-4 w-4 rounded-full border ${done ? "bg-[var(--adn-emerald)] border-[var(--adn-emerald)]" : "border-[var(--adn-line)]"} flex items-center justify-center`}>
                      {done && <Check size={10} className="text-white" />}
                    </span>
                    <span className={done ? "text-[var(--adn-text)]" : "text-[var(--adn-text-muted)]"}>{step}</span>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="bg-white border border-[var(--adn-line)] p-6">
            <p className="eyebrow">Documents</p>
            <p className="text-sm text-[var(--adn-text-soft)] mt-2">{(prop.document_urls || []).length} uploaded</p>
            <Link to="/messages" className="mt-4 inline-flex items-center gap-2 btn-outline-navy px-4 py-2 rounded text-xs">
              <MessageSquare size={14} /> Messages
            </Link>
          </section>
        </aside>
      </div>

      {showOffer && <SubmitOfferModal pid={pid} onClose={() => setShowOffer(false)} onSubmitted={() => { setShowOffer(false); load(); }} />}
      {contactCtx && <QuickMessageModal recipient_name={contactCtx.recipient_name} onClose={() => setContactCtx(null)} onSend={sendQuickMsg} />}
      {apptCtx && <ScheduleApptModal onClose={() => setApptCtx(null)} onSave={saveAppt} />}
    </DashboardLayout>
  );
}

function Stat({ label, v, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-[var(--adn-text-muted)]">{label}</div>
      <div className={`font-display tabular ${accent ? "text-[var(--adn-gold)] text-lg" : ""}`}>{v ?? "—"}</div>
    </div>
  );
}

function OfferRow({ offer, onAccept, onReject, canRespond, onMessage, onSchedule }) {
  const t = OFFER_TYPES.find(x => x.id === offer.offer_type)?.label || offer.offer_type;
  const statusPill =
    offer.status === "accepted" ? "pill-accepted" :
    offer.status === "rejected" ? "pill-rejected" :
    "pill-pending";
  return (
    <div className="border-l-4 border-l-[var(--adn-gold)] border-y border-r border-[var(--adn-line)] p-4 bg-white">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-display font-semibold">{t}</span>
            <span className={`pill ${statusPill}`}>{offer.status}</span>
          </div>
          <div className="text-xs text-[var(--adn-text-muted)] mt-1">{offer.provider_name} · {offer.provider_company || offer.provider_role}</div>
        </div>
        <div className="grid grid-cols-3 gap-5 text-sm">
          <Stat label="Amount" v={fmtUSD(offer.amount)} />
          <Stat label="Close" v={offer.closing_days ? `${offer.closing_days}d` : "—"} />
          <Stat label="Est. net" v={fmtUSD(offer.estimated_net)} />
        </div>
      </div>
      {offer.conditions && <p className="text-sm text-[var(--adn-text-soft)] mt-3">{offer.conditions}</p>}
      {offer.notes && <p className="text-sm text-[var(--adn-text-soft)] mt-2 italic">"{offer.notes}"</p>}
      <div className="text-[11px] text-[var(--adn-text-muted)] mt-3 flex items-center gap-1"><Clock size={12} /> expires {fmtDate(offer.expires_at)}</div>
      {canRespond && (
        <div className="mt-3 flex flex-wrap gap-2">
          {offer.status === "pending" && (
            <>
              <button data-testid={`accept-${offer.offer_id}`} onClick={onAccept} className="btn-gold px-4 py-2 rounded text-xs">Accept</button>
              <button data-testid={`reject-${offer.offer_id}`} onClick={onReject} className="btn-outline-navy px-4 py-2 rounded text-xs">Decline</button>
            </>
          )}
          <button data-testid={`msg-${offer.offer_id}`} onClick={onMessage} className="btn-outline-navy px-4 py-2 rounded text-xs inline-flex items-center gap-1">
            <MessageSquare size={12} /> Message
          </button>
          <button data-testid={`appt-${offer.offer_id}`} onClick={onSchedule} className="btn-outline-navy px-4 py-2 rounded text-xs inline-flex items-center gap-1">
            <Calendar size={12} /> Schedule
          </button>
        </div>
      )}
    </div>
  );
}

function QuickMessageModal({ recipient_name, onClose, onSend }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!body.trim()) return;
    setBusy(true);
    await onSend(body);
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-[var(--adn-navy)]/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white border border-[var(--adn-line)] max-w-md w-full p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-semibold">Message {recipient_name}</h3>
          <button type="button" onClick={onClose}><X /></button>
        </div>
        <textarea
          data-testid="quick-msg-body"
          autoFocus
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Hi — I'd like to discuss this property…"
          className="mt-4 w-full border border-[var(--adn-line)] p-3 text-sm min-h-[120px]"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-outline-navy px-5 py-2.5 rounded text-sm">Cancel</button>
          <button data-testid="quick-msg-send" type="submit" disabled={busy || !body.trim()} className="btn-gold px-5 py-2.5 rounded text-sm inline-flex items-center gap-2">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send
          </button>
        </div>
      </form>
    </div>
  );
}

function ScheduleApptModal({ onClose, onSave }) {
  const [f, setF] = useState({ start_at: "", duration_min: 30, location: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!f.start_at) return;
    setBusy(true);
    const iso = new Date(f.start_at).toISOString();
    await onSave({ start_at: iso, duration_min: parseInt(f.duration_min) || 30, location: f.location, notes: f.notes });
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-[var(--adn-navy)]/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white border border-[var(--adn-line)] max-w-md w-full p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-semibold">Schedule appointment</h3>
          <button type="button" onClick={onClose}><X /></button>
        </div>
        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="text-xs font-semibold text-[var(--adn-text-soft)] mb-1.5 block uppercase tracking-wider">Date & time</span>
            <input data-testid="appt-time" required type="datetime-local" value={f.start_at} onChange={(e) => setF({...f, start_at: e.target.value})} className="w-full border border-[var(--adn-line)] p-2.5 text-sm" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-[var(--adn-text-soft)] mb-1.5 block uppercase tracking-wider">Duration (min)</span>
            <input type="number" value={f.duration_min} onChange={(e) => setF({...f, duration_min: e.target.value})} className="w-full border border-[var(--adn-line)] p-2.5 text-sm" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-[var(--adn-text-soft)] mb-1.5 block uppercase tracking-wider">Location</span>
            <input value={f.location} onChange={(e) => setF({...f, location: e.target.value})} placeholder="On-site / phone / Zoom" className="w-full border border-[var(--adn-line)] p-2.5 text-sm" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-[var(--adn-text-soft)] mb-1.5 block uppercase tracking-wider">Notes</span>
            <textarea value={f.notes} onChange={(e) => setF({...f, notes: e.target.value})} className="w-full border border-[var(--adn-line)] p-2.5 text-sm min-h-[80px]" />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-outline-navy px-5 py-2.5 rounded text-sm">Cancel</button>
          <button data-testid="appt-save" type="submit" disabled={busy} className="btn-gold px-5 py-2.5 rounded text-sm inline-flex items-center gap-2">
            {busy && <Loader2 size={14} className="animate-spin" />} Schedule
          </button>
        </div>
      </form>
    </div>
  );
}

function SubmitOfferModal({ pid, onClose, onSubmitted }) {
  const [f, setF] = useState({ offer_type: "cash", amount: "", closing_days: 14, conditions: "", estimated_net: "", notes: "", expires_in_days: 14 });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const payload = { property_id: pid, ...f };
      ["amount","estimated_net"].forEach(k => payload[k] = payload[k] === "" ? null : parseFloat(payload[k]));
      ["closing_days","expires_in_days"].forEach(k => payload[k] = payload[k] === "" ? null : parseInt(payload[k]));
      await api.post("/offers", payload);
      toast.success("Offer submitted");
      onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[var(--adn-navy)]/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white border border-[var(--adn-line)] max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-semibold">Submit an offer</h3>
          <button type="button" onClick={onClose}><X /></button>
        </div>
        <div className="mt-5 space-y-4">
          <Lbl k="Offer type">
            <select className="modal-input" value={f.offer_type} onChange={(e)=>setF({...f, offer_type: e.target.value})}>
              {OFFER_TYPES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </Lbl>
          <div className="grid grid-cols-2 gap-4">
            <Lbl k="Amount ($)"><input data-testid="offer-amount" required type="number" className="modal-input" value={f.amount} onChange={(e)=>setF({...f, amount: e.target.value})}/></Lbl>
            <Lbl k="Closing days"><input type="number" className="modal-input" value={f.closing_days} onChange={(e)=>setF({...f, closing_days: e.target.value})}/></Lbl>
          </div>
          <Lbl k="Estimated net to seller ($)"><input type="number" className="modal-input" value={f.estimated_net} onChange={(e)=>setF({...f, estimated_net: e.target.value})}/></Lbl>
          <Lbl k="Conditions"><input className="modal-input" value={f.conditions} onChange={(e)=>setF({...f, conditions: e.target.value})} placeholder="As-is, no inspection contingency"/></Lbl>
          <Lbl k="Notes"><textarea className="modal-input min-h-[80px]" value={f.notes} onChange={(e)=>setF({...f, notes: e.target.value})}/></Lbl>
          <Lbl k="Expires in (days)"><input type="number" className="modal-input" value={f.expires_in_days} onChange={(e)=>setF({...f, expires_in_days: e.target.value})}/></Lbl>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-outline-navy px-5 py-2.5 rounded text-sm">Cancel</button>
          <button data-testid="submit-offer" type="submit" disabled={busy} className="btn-gold px-5 py-2.5 rounded text-sm inline-flex items-center gap-2">
            {busy && <Loader2 size={14} className="animate-spin" />} Submit Offer
          </button>
        </div>
        <style>{`.modal-input { width: 100%; background: white; border: 1px solid #CBD5E1; padding: 10px 12px; font-size: 14px; } .modal-input:focus { outline: none; border-color: var(--adn-navy); }`}</style>
      </form>
    </div>
  );
}

function Lbl({ k, children }) {
  return <label className="block"><span className="text-xs font-semibold text-[var(--adn-text-soft)] mb-1.5 block uppercase tracking-wider">{k}</span>{children}</label>;
}
